import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import API_URL from '../config/api';

const AddCar = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: '',
    mileage: '',
    vin: '',
    bodyType: '',
    location: '',
    color: '',
    fuelType: '',
    engineCapacity: '',
    enginePower: '',
    transmission: '',
    driveType: '',
    numberOfDoors: '',
    seatingCapacity: '',
    description: '',
    startingPrice: '',
    status: 'available',
    images: [],
    requiredParticipants: 300,
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedCarfax, setSelectedCarfax] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);
  };

  const handleCarfaxChange = (e) => {
    setSelectedCarfax(e.target.files[0]);
  };

  const uploadFiles = async () => {
    try {
      const imageUploads = selectedImages.map(async (image) => {
        const formData = new FormData();
        formData.append('images', image);
        
        const response = await fetch(`${API_URL}/api/upload/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Nahrávání obrázků selhalo');
        }
        
        const data = await response.json();
        return data.imageUrls[0];
      });

      let carfaxUrl = null;
      if (selectedCarfax) {
        const formData = new FormData();
        formData.append('carfax', selectedCarfax);
        
        const response = await fetch(`${API_URL}/api/upload/carfax`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Nahrávání CARFAX souboru selhalo');
        }
        
        const data = await response.json();
        carfaxUrl = data.fileUrl;
      }

      const uploadedImages = await Promise.all(imageUploads);
      return { images: uploadedImages, carfax: carfaxUrl };
    } catch (error) {
      console.error('Chyba při nahrávání:', error);
      throw new Error(`Chyba při nahrávání souborů: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!auth.currentUser) {
        throw new Error('Musíte být přihlášeni pro přidání auta');
      }

      // Nahrání souborů
      const { images, carfax } = await uploadFiles();

      // Vytvoření auta
      const carResponse = await fetch(`${API_URL}/api/cars`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          ...formData,
          images,
          carfax,
          year: parseInt(formData.year),
          mileage: parseInt(formData.mileage),
          engineCapacity: parseInt(formData.engineCapacity),
          numberOfDoors: formData.numberOfDoors ? parseInt(formData.numberOfDoors) : undefined,
          seatingCapacity: formData.seatingCapacity ? parseInt(formData.seatingCapacity) : undefined,
          startingPrice: parseInt(formData.startingPrice),
          requiredParticipants: parseInt(formData.requiredParticipants),
          sellerId: auth.currentUser.uid
        })
      });

      if (!carResponse.ok) {
        const errorData = await carResponse.json();
        throw new Error(errorData.message || 'Chyba při vytváření auta');
      }

      const { car } = await carResponse.json();

      // Vytvoření aukce
      const auctionResponse = await fetch(`${API_URL}/api/auctions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          carId: car._id,
          sellerId: auth.currentUser.uid,
          currentBid: parseInt(formData.startingPrice),
          requiredParticipants: parseInt(formData.requiredParticipants)
        })
      });

      if (!auctionResponse.ok) {
        const errorData = await auctionResponse.json();
        throw new Error(errorData.message || 'Chyba při vytváření aukce');
      }

      alert('Auto bylo úspěšně přidáno a aukce vytvořena');
      navigate('/');
    } catch (error) {
      console.error('Chyba:', error);
      setError(error.message || 'Nastala neočekávaná chyba');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6 text-center">Přidat nové auto</h2>
        
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Značka</label>
              <input
                type="text"
                name="make"
                value={formData.make}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Model</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">VIN</label>
              <input
                type="text"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Rok výroby</label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Najeté kilometry</label>
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Karosérie</label>
              <select
                name="bodyType"
                value={formData.bodyType}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Vyberte typ karosérie</option>
                <option value="sedan">Sedan</option>
                <option value="kombi">Kombi</option>
                <option value="hatchback">Hatchback</option>
                <option value="suv">SUV</option>
                <option value="coupe">Kupé</option>
                <option value="cabrio">Kabriolet</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Umístění vozidla</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Barva</label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Palivo</label>
              <select
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Vyberte typ paliva</option>
                <option value="benzin">Benzín</option>
                <option value="diesel">Diesel</option>
                <option value="hybrid">Hybrid</option>
                <option value="electric">Elektřina</option>
                <option value="lpg">LPG</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Objem motoru (ccm)</label>
              <input
                type="number"
                name="engineCapacity"
                value={formData.engineCapacity}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Výkon (např. "210 kw (282 koní)")</label>
              <input
                type="text"
                name="enginePower"
                value={formData.enginePower}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Převodovka</label>
              <select
                name="transmission"
                value={formData.transmission}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Vyberte typ převodovky</option>
                <option value="manual">Manuální</option>
                <option value="automatic">Automatická</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Typ pohonu</label>
              <select
                name="driveType"
                value={formData.driveType}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Vyberte typ pohonu</option>
                <option value="fwd">Přední pohon</option>
                <option value="rwd">Zadní pohon</option>
                <option value="awd">4x4</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Počet dveří</label>
              <input
                type="number"
                name="numberOfDoors"
                value={formData.numberOfDoors}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Počet míst</label>
              <input
                type="number"
                name="seatingCapacity"
                value={formData.seatingCapacity}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Vyvolávací cena</label>
              <input
                type="number"
                name="startingPrice"
                value={formData.startingPrice}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Popis</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Požadovaný počet účastníků pro spuštění aukce
            </label>
            <input
              type="number"
              name="requiredParticipants"
              value={formData.requiredParticipants}
              onChange={handleChange}
              min="2"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Fotografie vozu (max. 10)
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
            />
            <p className="mt-2 text-sm text-gray-500">
              Můžete nahrát až 10 fotografií. Podporované formáty: JPG, PNG
            </p>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              CARFAX Report (PDF)
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleCarfaxChange}
              className="mt-1 block w-full"
            />
            <p className="mt-2 text-sm text-gray-500">
              Nahrajte CARFAX report ve formátu PDF
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Přidávám...' : 'Přidat auto'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddCar; 